<script setup>
import AppRouterView from 'components/AppRouterView.vue';
import { usePageMeta } from 'composables/page-meta';
import { Dark, useQuasar } from 'quasar';
import seoConfig from 'src/configs/seo';
import { formatPageTitle } from 'src/utils/page-meta-util';
import { buildRouteRobotsMeta } from 'src/utils/route-meta-util';
import { useAppStore } from 'stores/app-store';
import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

// Initialize the AppStore, so it can watch global configuration values.
const $q = useQuasar();
const $route = useRoute();
const appStore = useAppStore();

// Preload the service dialogs in case of a problem, as they may not be dynamically loadable during an outage.
import('components/service-dialog/NewVersionServiceDialog.vue');
import('components/service-dialog/UnavailableServiceDialog.vue');

const routerViewKey = ref(0);

const serviceDialogIsActive = computed(() => !!serviceDialogComponent.value);

const serviceDialogComponent = computed(() => {
    if (!!appStore.unavailableServiceDialog) return defineAsyncComponent(() => import('components/service-dialog/UnavailableServiceDialog.vue'));
    if (!!appStore.newVersionServiceDialog) return defineAsyncComponent(() => import('components/service-dialog/NewVersionServiceDialog.vue'));
    return null;
});

const serviceDialogProps = computed(() => {
    return !!appStore.unavailableServiceDialog
        ? appStore.unavailableServiceDialog ?? {}
        : {};
});

// Quasar owns lang and ignores Meta's htmlAttr.lang; its setter also scopes the change to the current SSR request.
$q.lang.set({ ...$q.lang, isoName: seoConfig.language });

usePageMeta({ titleTemplate: formatPageTitle });

usePageMeta(() => buildRouteRobotsMeta($route));

onMounted(() => {
    Dark.set(appStore.darkMode);
    appStore.watchForThemeChanges();
});

watch(serviceDialogIsActive, (newVal, oldVal) => {
    if (newVal === false && oldVal === true) {
        routerViewKey.value = routerViewKey.value + 1;
    }
});

function appendMaterialSymbolsRoundedLoadedClass() {
    if (!import.meta.env.QUASAR_CLIENT) {
        return;
    }
    document.documentElement.classList.add('material-symbols-rounded-loaded');
}

async function watchMaterialSymbolsRoundedLoaded() {
    if (!import.meta.env.QUASAR_CLIENT) {
        return;
    }
    // As the icons are loaded from a third party source, the user may experience FOUT (Flash of Un-styled Text) which
    // displays the underlying text within the icon space before the font has loaded. This hack improves this.
    const FontFaceObserver = (await import('fontfaceobserver'))?.default;
    if (!FontFaceObserver) {
        // If for some reason the library cannot be loaded, then append the class anyway to ensure the user is not left
        // without icons.
        appendMaterialSymbolsRoundedLoadedClass();
        return;
    }
    const font = new FontFaceObserver('Material Symbols Rounded');
    font.load().then(() => appendMaterialSymbolsRoundedLoadedClass());
}

watchMaterialSymbolsRoundedLoaded();
</script>

<template>
    <AppRouterView :key="routerViewKey"/>
    <component v-if="serviceDialogIsActive" :is="serviceDialogComponent" v-model="serviceDialogIsActive" v-bind="serviceDialogProps"/>
</template>
